import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IMAGE_URL } from "network";
import { useEffect, useState } from "react";
import { categoryOptions } from "../shopcategory/data";
import { useGetShopCategoriesQuery } from "slices/eCommerce/shopCatSlice";

const steps = ["Select Category For", "Category Details", "SEO Data"];

function AddForm({ open, onClose, initialData, onSubmit }) {
  const { data, isLoading, refetch } = useGetShopCategoriesQuery({});

  const [previewUrl, setPreviewUrl] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [step, setStep] = useState(0);

  const validationSchemas = [
    Yup.object({
      category: Yup.string().required("Parent category is required"),
      useFor: Yup.string().required("This field is required"),
    }),
    Yup.object({
      name: Yup.string().required("Category name is required"),
      image: Yup.mixed()
        .nullable()
        .test("fileRequired", "Image is required", (value) => {
          return initialData?.image || value;
        }),
    }),
    Yup.object({
      metaTitle: Yup.string().required("Meta title is required"),
      metaDescription: Yup.string().required("Meta description is required"),
    }),
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      _id: initialData?._id || "",
      name: initialData?.name || "",
      image: null,
      metaTitle: initialData?.metaTitle || "",
      metaDescription: initialData?.metaDescription || "",
      keywords: initialData?.keywords || [],
      useFor: initialData?.useFor || "",
      category: initialData?.category || "",
    },
    validationSchema: validationSchemas[step],
    onSubmit: async (values) => {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length) return;
      await onSubmit(values);
      handleCancel();
    },
  });

  useEffect(() => {
    if (initialData?.image) {
      setPreviewUrl(IMAGE_URL + initialData.image);
    }
  }, [initialData]);

  const addKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !formik.values.keywords.includes(trimmed)) {
      formik.setFieldValue("keywords", [...formik.values.keywords, trimmed]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (kw) => {
    formik.setFieldValue(
      "keywords",
      formik.values.keywords.filter((k) => k !== kw)
    );
  };

  const handleFileChange = (field, file) => {
    formik.setFieldValue(field, file);
    if (file && field === "image") {
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleNext = async () => {
    const fields = Object.keys(validationSchemas[step].fields);

    // Set touched state for all fields in current step
    const touchedState = {};
    fields.forEach((field) => {
      touchedState[field] = true;
    });
    formik.setTouched(touchedState);

    // Validate current step
    const errors = await formik.validateForm();
    const stepErrors = fields.some((field) => errors[field]);

    if (!stepErrors) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setStep((prev) => prev - 1);

  const handleCancel = () => {
    formik.resetForm();
    setPreviewUrl("");
    onClose();
    setStep(0);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? "Edit Sub Category" : "Add Sub Category"}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Stepper activeStep={step} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {step === 0 && (
            <Box mt={3}>
              <FormControl
                fullWidth
                margin="normal"
                required
                error={
                  formik.touched.category && Boolean(formik.errors.category)
                }
              >
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  name="category"
                  labelId="category-label"
                  value={formik.values.category || ""}
                  label="Category"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {data?.data && data.data.length > 0 ? (
                    data.data.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No categories found</MenuItem>
                  )}
                </Select>
                {formik.touched.category && formik.errors.category && (
                  <Typography color="error" variant="caption">
                    {formik.errors.category}
                  </Typography>
                )}
              </FormControl>

              <Typography variant="subtitle1" gutterBottom>
                Category For:
              </Typography>
              <div className="row g-2">
                {categoryOptions?.map((option, i) => (
                  <div className="col-4" key={i}>
                    <div
                      className={`card h-100 text-center p-2 border ${
                        formik.values.useFor === option.value
                          ? "border-primary shadow"
                          : "border-light"
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        formik.setFieldValue("useFor", option.value);
                        formik.setFieldTouched("useFor", true);
                      }}
                    >
                      <img
                        src={option.img}
                        className="card-img-top img-fluid"
                        alt={option.label}
                        style={{ height: "100px", objectFit: "cover" }}
                      />
                      <div className="card-body p-1">
                        <h6 className="card-title mb-0">{option.label}</h6>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {formik.touched.useFor && formik.errors.useFor && (
                <Typography color="error" variant="caption">
                  {formik.errors.useFor}
                </Typography>
              )}
            </Box>
          )}

          {step === 1 && (
            <Box mt={3}>
              <TextField
                name="name"
                label="Category Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                fullWidth
                margin="normal"
              />

              <Typography variant="subtitle1" mt={2}>
                Upload Image  (170*200)
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange("image", e.target.files[0])}
                onBlur={formik.handleBlur}
                style={{ marginTop: 8 }}
              />
              {formik.touched.image && formik.errors.image && (
                <Typography color="error" variant="caption">
                  {formik.errors.image}
                </Typography>
              )}
              {previewUrl && (
                <img
                  crossOrigin="anonymous"
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    marginTop: 16,
                    width: "100px",
                    maxHeight: 200,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
              )}
            </Box>
          )}

          {step === 2 && (
            <Box mt={3}>
              <TextField
                name="metaTitle"
                label="Meta Title"
                value={formik.values.metaTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.metaTitle && Boolean(formik.errors.metaTitle)
                }
                helperText={formik.touched.metaTitle && formik.errors.metaTitle}
                fullWidth
                margin="normal"
              />

              <TextField
                name="metaDescription"
                label="Meta Description"
                value={formik.values.metaDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.metaDescription &&
                  Boolean(formik.errors.metaDescription)
                }
                helperText={
                  formik.touched.metaDescription &&
                  formik.errors.metaDescription
                }
                fullWidth
                margin="normal"
              />

              <Box display="flex" alignItems="center" mt={2} gap={1}>
                <TextField
                  label="Add Keyword"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addKeyword();
                    }
                  }}
                  fullWidth
                />
                <Button
                  onClick={addKeyword}
                  variant="contained"
                  color="primary"
                >
                  Add
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {formik.values.keywords.map((keyword) => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    onDelete={() => removeKeyword(keyword)}
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          {step > 0 && (
            <Button onClick={handleBack} color="secondary">
              Back
            </Button>
          )}
          {step < steps.length - 1 && (
            <Button onClick={handleNext} variant="contained" color="primary">
              Next
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button type="submit" variant="contained" color="primary">
              {initialData ? "Update" : "Add"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AddForm;
